import { memo, useEffect, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    CatalogItemPriceRow,
    CatalogItemPricesTableCallbacks,
    CatalogItemPricesViewOptions,
} from "../catalogItemPrices.types";
import styles from "./PriceDescriptionCell.module.scss";

export const PriceDescriptionCell = memo(function PriceDescriptionCell({
    row,
    table,
}: CellContext<ViewTableFeatures, CatalogItemPriceRow, unknown>) {
    const { callbacks } = useViewTableMeta<
        CatalogItemPricesTableCallbacks,
        CatalogItemPricesViewOptions
    >(table);
    const price = row.original;
    const [value, setValue] = useState(price.description ?? "");

    useEffect(() => {
        setValue(price.description ?? "");
    }, [price.description]);

    // Description entry at draft-creation time is deferred: the add-row flow
    // only takes an amount today, so a new price's description is set via
    // this column's inline edit after the price is created.
    if (price.isDraft) {
        return <ViewTableCell />;
    }

    const handleBlur = async () => {
        const trimmed = value.trim();
        const nextDescription = trimmed.length > 0 ? trimmed : null;
        if (nextDescription === (price.description ?? null)) {
            setValue(trimmed);
            return;
        }
        const didUpdate = await callbacks?.onUpdateDescription(
            price,
            nextDescription,
        );
        // On success the row's persisted description updates and the sync
        // effect resets the input; on failure restore the persisted value so
        // the table never shows an edit that was never saved.
        if (!didUpdate) {
            setValue(price.description ?? "");
        }
    };

    return (
        <ViewTableCell captureClicks>
            <input
                type="text"
                className={styles.input}
                value={value}
                placeholder="Add description"
                onChange={(event) => setValue(event.target.value)}
                onBlur={() => void handleBlur()}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.currentTarget.blur();
                    }
                }}
            />
        </ViewTableCell>
    );
});
